import {
  Document,
  Font,
  Image as ReactPdfImage,
  Page,
  pdf,
  StyleSheet,
  Text,
  View,
} from '@react-pdf/renderer';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { chunk } from 'underscore';

import { formattedDate } from '../../../../../../utils/dateUtils';
import { extractPlainTextFromHtmlString, normalizeString } from '../../../../../../utils/text/text';
import {
  SVGLineData,
  SVGPoiData,
  SVGPointData,
  SVGTextData,
  SVGTriangleData,
} from '../../../../../../utils/three/vectorize-geometry';
import { ViewerEnvironmentInstance } from '../../../../../../viewer/viewer-environment';
import { useInlineFont } from '../../../../../hooks/use-inline-font';
import { projectStateAdapter } from '../../../../../state/project-state/project-state-adapter';
import { reset } from '../../../../../utils/styled-reset';
import { useWmsAttributions } from '../../../../wms-map/wms-attributions';
import { profileReportUtils } from '../../profile-section-tool-menu/profile-section-pdf';
import type { GraphLine } from '../../profile-section-tool-menu/profile-section-tool-menu';
import { pdfPageDimensions } from '../helpers/types';
import { downloadBlob } from '../helpers/utils';
import { CadDrawingReportImage } from './cad-drawing-image-utils';
import { InfoPanelOptions } from './cad-drawing-pdf-report-menu';
import { CadDrawingScaleBar } from './cad-drawing-scale-bar';
import { NorthArrow } from './north-arrow';
import { PoiMarker } from './poi-marker';
import { PoiThumbnail } from './poi-thumbnail';

type Props = {
  image: CadDrawingReportImage;
  preview: boolean;
  options: InfoPanelOptions;
};

Font.register({
  family: 'Inter24p',
  src: '/static/fonts/truetype/Inter_24pt-Regular.ttf',
});

const ReactPdfWmsAttributions = () => {
  const { t } = useTranslation();
  const attributions = useWmsAttributions();

  if (attributions.length === 0) {
    return null;
  }
  const clearHtmlAndNewLines = (htmlText: string) =>
    extractPlainTextFromHtmlString(htmlText).replaceAll(/\n/g, '').trim();
  return (
    <View style={styles.wmsAttributions}>
      <Text>{t('cadDrawingReport.backgroundMapProvidedBy', { ns: 'skyviewReport' })}</Text>
      {attributions.map((item, idx) => (
        <Text key={idx}>{clearHtmlAndNewLines(item)}</Text>
      ))}
      <Text>({formattedDate(Date.now())})</Text>
    </View>
  );
};

const CadDrawingPdfReport = (props: Props) => {
  return (
    <Document style={styles.document}>
      <Page dpi={72} orientation="landscape" size="A3" style={styles.page}>
        <View style={styles.content}>
          <ReactPdfWmsAttributions />
          <View style={styles.graphBoxCad}>
            <CadImage preview={props.preview} src={props.image.url} />
          </View>
          <InfoPanel options={props.options} style={styles.infoPanelAbsolute} />
          <CadDrawingScaleBar image={props.image} />
          {props.options.isOrthoView && (
            <NorthArrow
              invertBorderColor={props.image.backgroundColor === '#000000'}
              preview={props.preview}
            />
          )}
        </View>
      </Page>
    </Document>
  );
};

const ProfileGraphImage = (props: { dataUrl: string }) => {
  return <ReactPdfImage src={props.dataUrl} style={styles.profileGraphImage} />;
};

const normalizeHex = (c: string) => (c?.startsWith('#') ? c : `#${c ?? ''}`);

const LegendRow = ({ name, color }: GraphLine) => {
  // React-PDF doesn’t support dashed borders reliably; keep solid sample
  const stroke = normalizeHex(color.getHexString());
  return (
    <View style={styles.legendRow}>
      <View style={[styles.legendLine, { backgroundColor: stroke }]} />
      <Text style={styles.legendName}>{name}</Text>
    </View>
  );
};

const LegendPanel = (props: { options: ProfileLineReportOptions }) => {
  return (
    <View style={styles.legendPanel}>
      <Text style={styles.legendTitle}>{props.options.legendTitle}</Text>

      <View style={styles.legendScrollArea}>
        {props.options.legendItems.map((it, i) => (
          <LegendRow key={i} {...it} />
        ))}
      </View>

      {/* Footer info box pinned to bottom of legend */}
      <InfoPanel
        options={{
          freeText: '',
          profileLineName: props.options.footer.profileLineName ?? '',
          totalLength: props.options.footer.totalLength ?? '',
          description: props.options.footer.description ?? '',
          includeDrawingFrame: true,
          useVectorizedDrawing: false,
          useOriginalLineColor: false,
          strokeWidth: 1,
          responsible: props.options.footer.author,
          title: props.options.footer.title,
          useWhiteBackground: true,
          isOrthoView: true,
          includeOrthophoto: true,
        }}
      />
    </View>
  );
};

export const ProfileLinePdfReport = (props: {
  imageUrls: string[];
  options: ProfileLineReportOptions;
}) => {
  return (
    <Document style={styles.document}>
      {props.imageUrls.map((imageUrl, idx) => (
        <Page dpi={72} key={idx} orientation="landscape" size="A3" style={styles.page}>
          <View style={styles.content}>
            <View style={styles.graphBox}>
              <ProfileGraphImage dataUrl={imageUrl} />
            </View>
            <LegendPanel options={props.options} />
          </View>
        </Page>
      ))}
    </Document>
  );
};

export const ProfileLinePdfReportSvg = (props: {
  svgs: JSX.Element[];
  options: ProfileLineReportOptions;
  rows: number;
}) => {
  const chunks = chunk(props.svgs, props.rows * props.rows) as JSX.Element[][];
  return (
    <Document style={styles.document}>
      {chunks.map((svgChunk, idx) => (
        <Page dpi={72} key={idx} orientation="landscape" size="A3" style={styles.page}>
          <View style={styles.content}>
            <View style={styles.graphArea}>
              <View
                style={[
                  styles.graphsGrid,
                  { flexDirection: 'column', flexWrap: 'wrap' }, // column-first flow
                ]}
              >
                {svgChunk.map((svgEl, i) => (
                  <View
                    key={i}
                    style={[
                      styles.graphsCell,
                      {
                        width: `${100 / props.rows}%`,
                        height: `${100 / props.rows}%`,
                      },
                    ]}
                  >
                    <View style={styles.graphsCellInner}>{svgEl}</View>
                  </View>
                ))}
              </View>
            </View>
            <LegendPanel options={props.options} />
          </View>
        </Page>
      ))}
    </Document>
  );
};

/**
 * Since image tags works a little bit different in the exported
 * PDF and in the browser we return a different element for the Image
 * based on if it's a preview or not.
 */
const CadImage = (props: { src: string; preview: boolean }) => {
  return props.preview ? (
    <PreviewImage src={props.src} />
  ) : (
    <ReactPdfImage src={props.src} style={styles.image} />
  );
};

const InfoPanel = (props: {
  style?: (typeof styles)['infoPanelAbsolute'];
  options: InfoPanelOptions;
}) => {
  const { t } = useTranslation();
  if (!props.options.includeDrawingFrame) {
    return null;
  }

  return (
    <View style={props.style}>
      {props.options.uploadedImage && (
        <View style={styles.uploadedImageContainer}>
          {props.options.preview ? (
            <ScaledImagePreview dataUrl={props.options.uploadedImage.dataUrl} />
          ) : (
            <ReactPdfImage src={props.options.uploadedImage.dataUrl} style={styles.uploadedImage} />
          )}
        </View>
      )}
      <FreeTextField text={props.options.freeText} />

      <InfoPanelField
        label={t('cadDrawingReport.pdf.rightPanel.title', { ns: 'skyviewReport' })}
        text={props.options.title}
      />

      <InfoPanelField
        label={t('cadDrawingReport.pdf.rightPanel.projectName', { ns: 'skyviewReport' })}
        text={projectStateAdapter.getSnapshot().project?.name ?? ''}
      />

      <InfoPanelField
        label={t('profileSection.profileLine', { ns: 'skyview' })}
        text={props.options.profileLineName}
      />

      <InfoPanelField
        label={t('designQuantityReport.report.length', {
          ns: 'skyviewReport',
          unit: ViewerEnvironmentInstance.getUnitSymbols().length,
        })}
        text={props.options.totalLength}
      />

      <InfoPanelField
        label={t('cadDrawingReport.pdf.rightPanel.description', { ns: 'skyviewReport' })}
        text={props.options.description}
      />

      <View style={styles.infoPanelField}>
        <View style={styles.dateField}>
          <Text style={styles.fieldLabel}>
            {t('cadDrawingReport.pdf.rightPanel.date', { ns: 'skyviewReport' })}
          </Text>
          <Text style={styles.textSmall}>{formattedDate(Date.now())}</Text>
        </View>
        <View style={styles.fieldItemContainer}>
          <Text style={styles.fieldLabel}>
            {t('cadDrawingReport.pdf.rightPanel.responsible', { ns: 'skyviewReport' })}
          </Text>
          <Text style={styles.textSmall}>{props.options.responsible}</Text>
        </View>
      </View>
    </View>
  );
};

export const InfoPanelField = (props: { label: string; text: string }) => {
  if (props.text.trim().length === 0) {
    return null;
  }

  return (
    <View style={styles.infoPanelField}>
      <View style={styles.fieldItemContainer}>
        <Text style={styles.fieldLabel}>{props.label}</Text>
        <Text style={styles.textMedium}>{props.text}</Text>
      </View>
    </View>
  );
};

export const FreeTextField = (props: { text: string }) => {
  if (props.text.trim().length === 0) {
    return null;
  }

  return <View style={styles.freeTextField}>{renderTextWithLinebreaks(props.text)}</View>;
};

const ScaledImagePreview = (props: { dataUrl: string }) => {
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setDimensions({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.src = props.dataUrl;
  }, [props.dataUrl]);

  if (!dimensions) {
    return <div style={{ width: '100%', maxHeight: '80px', backgroundColor: '#f0f0f0' }} />;
  }

  // Calculate the viewBox to maintain aspect ratio
  const aspectRatio = dimensions.width / dimensions.height;
  const maxWidth = 100;
  const maxHeight = 100;

  let viewBoxWidth = maxWidth;
  let viewBoxHeight = maxHeight;

  if (aspectRatio > 1) {
    // Landscape image - fit to width
    viewBoxHeight = maxWidth / aspectRatio;
  } else {
    // Portrait or square image - fit to height
    viewBoxWidth = maxHeight * aspectRatio;
  }

  return (
    <svg
      preserveAspectRatio="xMidYMid meet"
      style={{ width: '100%', maxHeight: '80px', display: 'block' }}
      viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
    >
      <image height={viewBoxHeight} href={props.dataUrl} width={viewBoxWidth} x="0" y="0" />
    </svg>
  );
};

function renderTextWithLinebreaks(text: string) {
  return text.split('\n').map((x, idx) => {
    return (
      <Text key={idx} style={styles.freeText}>
        {x}
      </Text>
    );
  });
}

export const CadDrawingVectorizedPreview = (props: {
  lines: SVGLineData[];
  triangles: SVGTriangleData[];
  texts: SVGTextData[];
  points: SVGPointData[];
  pois: SVGPoiData[];
  backgroundImage: CadDrawingReportImage;
  width: number;
  height: number;
  backgroundColor?: string;
  options: InfoPanelOptions;
}) => {
  useInlineFont('/static/fonts/truetype/Inter_24pt-Regular.ttf', 'Inter24p');
  const defaultColor = props.backgroundColor === '#000000' ? 'white' : 'black';
  // Create SVG elements for preview
  const svgLines = props.lines.map((line, index) => (
    <line
      key={`line-${index}`}
      stroke={line.color || defaultColor}
      strokeWidth={props.options.strokeWidth}
      x1={line.x1}
      x2={line.x2}
      y1={line.y1}
      y2={line.y2}
    />
  ));

  // Create SVG triangle elements for preview
  const svgTriangles = props.triangles.map((triangle, index) => (
    <polygon
      fill={triangle.fill || 'none'}
      key={`triangle-${index}`}
      points={`${triangle.x1},${triangle.y1} ${triangle.x2},${triangle.y2} ${triangle.x3},${triangle.y3}`}
      stroke={triangle.color || defaultColor}
      strokeWidth={props.options.strokeWidth}
    />
  ));

  // Create SVG text elements for preview
  const svgTexts = props.texts.map((text, index) => {
    // Build transform string for rotation
    const transform = text.rotation
      ? `rotate(${text.rotation * (180 / Math.PI)} ${text.x} ${text.y})`
      : undefined;

    return (
      <text
        dominantBaseline={text.dominantBaseline || 'auto'}
        fill={text.color || defaultColor}
        fontFamily="Inter24p"
        fontSize={text.fontSize}
        fontStyle={text.fontStyle}
        fontWeight={text.fontWeight}
        key={`text-${index}`}
        textAnchor={text.textAnchor || 'start'}
        transform={transform}
        x={text.x}
        y={text.y}
      >
        {text.text}
      </text>
    );
  });

  // Create SVG square elements for points
  const svgPoints = props.points.map((point, index) => {
    const size = props.options.strokeWidth * 1.15;
    const halfSize = size / 2;
    return (
      <rect
        fill={point.color || defaultColor}
        height={size}
        key={`point-${index}`}
        width={size}
        x={point.x - halfSize}
        y={point.y - halfSize}
      />
    );
  });

  // Filter POI markers - only include those without thumbnailUrl
  const poisWithoutThumbnails = props.pois.filter((poi) => !poi.thumbnailUrl);
  const poisWithThumbnails = props.pois.filter((poi) => poi.thumbnailUrl);

  // Create POI marker components for POIs without thumbnails
  const poiMarkers = poisWithoutThumbnails.map((poi, index) => (
    <PoiMarker key={`poi-${index}`} poi={poi} preview={true} />
  ));

  return (
    <PreviewContainer>
      <CadDrawingVectorized
        backgroundColor={props.backgroundColor}
        backgroundImage={props.backgroundImage}
        height={props.height}
        options={{
          ...props.options,
          preview: true,
        }}
        poisWithThumbnails={poisWithThumbnails}
        preview={true}
        svg={
          <svg
            preserveAspectRatio="xMidYMid meet"
            style={{
              width: '100%',
              height: '100%',
              display: 'block',
            }}
            viewBox={`${0} ${0} ${props.width} ${props.height}`}
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Background image - must be first in SVG to appear behind other elements */}
            <image
              height={props.height}
              href={props.backgroundImage.url}
              preserveAspectRatio="none"
              width={props.width}
              x="0"
              y="0"
            />
            {/* High-resolution orthophoto layer */}
            {props.backgroundImage.highResolutionOrthophotoUrl && (
              <image
                height={props.height}
                href={props.backgroundImage.highResolutionOrthophotoUrl}
                preserveAspectRatio="none"
                width={props.width}
                x="0"
                y="0"
              />
            )}

            {svgTriangles}
            {svgLines}
            {svgPoints}
            {svgTexts}
            {poiMarkers}
          </svg>
        }
        width={props.width}
      />
    </PreviewContainer>
  );
};

export const CadDrawingPdfReportPreview = (props: Omit<Props, 'preview'>) => {
  return (
    <PreviewContainer>
      <CadDrawingPdfReport
        image={props.image}
        options={{
          ...props.options,
          preview: true,
        }}
        preview={true}
      />
    </PreviewContainer>
  );
};

type CadDrawingVectorizedProps = {
  svg: React.ReactNode;
  texts?: React.ReactNode[];
  options: InfoPanelOptions;
  backgroundColor?: string;
  backgroundImage: CadDrawingReportImage;
  preview: boolean;
  width?: number;
  height?: number;
  poisWithThumbnails?: SVGPoiData[];
};

export const CadDrawingVectorized: React.FC<CadDrawingVectorizedProps> = ({
  svg,
  texts,
  options,
  backgroundColor,
  backgroundImage,
  width,
  height,
  preview,
  poisWithThumbnails = [],
}) => {
  return (
    <Document style={styles.document}>
      <Page dpi={72} orientation="landscape" size="A3" style={styles.page}>
        <View style={styles.content}>
          <ReactPdfWmsAttributions />
          <View
            style={{
              ...styles.graphBoxCad,
              ...(backgroundColor ? { backgroundColor } : {}),
              position: 'relative', // ensure a positioning context
            }}
          >
            {/* Background image layer */}
            {!preview && <ReactPdfImage src={backgroundImage.url} style={styles.imageVector} />}
            {/* High-resolution orthophoto layer */}
            {!preview && backgroundImage.highResolutionOrthophotoUrl && (
              <ReactPdfImage
                src={backgroundImage.highResolutionOrthophotoUrl}
                style={styles.imageVector}
              />
            )}
            {/* SVG layer on top */}
            {svg}
            {texts}
            {/* POI thumbnails positioned absolutely */}
            {poisWithThumbnails.map((poi, index) => (
              <PoiThumbnail
                key={`poi-thumbnail-${index}`}
                poi={poi}
                preview={preview}
                svgHeight={height}
                svgWidth={width}
              />
            ))}
          </View>
          <InfoPanel options={options} style={styles.infoPanelAbsolute} />
          <CadDrawingScaleBar image={backgroundImage} />
          {options.isOrthoView && (
            <NorthArrow invertBorderColor={backgroundColor === '#000000'} preview={preview} />
          )}
        </View>
      </Page>
    </Document>
  );
};

/**
 * Styling is adjusted so that the in browser preview
 * will resemble the PDF-report as closely as possible.
 */
const PreviewContainer = styled.div`
  ${reset}
  box-sizing: reset;
  font-family: helvetica;
  line-height: normal;
  overflow: auto;
  display: flex;
  flex-direction: row;
  position: relative;
  width: 100%;
  margin: 0 auto;
`;

const PreviewImage = styled.img`
  width: 100%;
  display: block;
  border: 1px solid #a5acaf;
`;

/**
 * Width in pixels for the info panel in the CAD drawing report.
 * This is the section where project name, author, description etc is written.
 */
const infoPanelWidth = Math.round(pdfPageDimensions.a3.landscape72Dpi.width * 0.2);
const pagePadding = pdfPageDimensions.a3.landscape72Dpi.defaultPadding;
/**
 * Styling is adjusted for 72 dpi resolution in the PDF.
 *
 * Note: react-pdf limits what stylings can be used for the PDF-export so
 * we can only use compatible stylings.
 */
const styles = StyleSheet.create({
  document: {
    display: 'flex',
    border: '1px solid #a5acaf',
  },
  page: {
    position: 'relative',
    padding: `${pagePadding}px`,
    width: `${pdfPageDimensions.a3.landscape72Dpi.width}px`,
    height: `${pdfPageDimensions.a3.landscape72Dpi.height}px`,
  },
  content: {
    display: 'flex',
    position: 'relative',
    flexDirection: 'row',
    flexGrow: 1,
    height: '100%',
  },
  graphBoxCad: {
    flexGrow: 1,
    border: '1px solid #a5acaf',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  graphBox: {
    flexGrow: 1,
    display: 'flex',
    borderTop: '1px solid #a5acaf',
    borderLeft: '1px solid #a5acaf',
    borderBottom: '1px solid #a5acaf',
  },

  graphArea: {
    flexGrow: 1,
    display: 'flex',
    padding: 6,
  },

  graphsGrid: {
    display: 'flex',
    flexGrow: 1,
  },

  graphsCell: {
    padding: 4,
  },

  graphsCellInner: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'space-evenly',
  },

  wmsAttributions: {
    // Aligns the attribution text just outside of the frame.
    position: 'absolute',
    width: '100%',
    left: '0',
    top: '-12px',
    fontSize: '8px',
    display: 'flex',
    gap: '2px',
    flexDirection: 'row',
  },
  image: {
    width: '100%',
  },
  imageVector: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  infoPanelAbsolute: {
    display: 'flex',
    backgroundColor: 'white',
    position: 'absolute',
    right: 0,
    bottom: 0,
    flexDirection: 'column',
    width: `${infoPanelWidth}px`,
    maxHeight: '100%',
    borderLeft: '1px solid #a5acaf',
    borderBottom: '1px solid #a5acaf',
    borderRight: '1px solid #a5acaf',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  legendPanel: {
    width: '250px',
    border: `1px solid #a5acaf`,
    borderLeft: `1px solid #a5acaf`,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  profileGraphImage: {
    flex: 1,
    objectFit: 'contain',
  },
  legendRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  legendLine: {
    width: 30,
    minWidth: 30,
    height: 3,
    marginRight: 8,
    flexShrink: 0,
    flexGrow: 0,
  },
  legendName: {
    fontSize: 10,
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 0,
    paddingRight: 2,
  },
  legendTitle: {
    fontSize: 12,
    marginTop: 14,
    marginLeft: 14,
    marginRight: 14,
    marginBottom: 6,
  },
  legendScrollArea: {
    flexGrow: 1,
    gap: 10,
    paddingLeft: 14,
    paddingRight: 40,
  },
  /**
   * This is the small label in the top left corner of a field in the cad drawing info panel.
   */
  fieldLabel: {
    width: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    fontSize: '6px',
    textAlign: 'left',
  },
  freeTextField: {
    display: 'flex',
    position: 'relative',
    flexDirection: 'column',
    padding: '2px',
    width: '100%',
    flex: 1,
    borderTop: '1px solid #a5acaf',
    boxSizing: 'border-box',
  },
  freeText: {
    fontSize: '12px',
    textAlign: 'left',
    minHeight: '14px', // This forces an empty row when a row is just white space.
  },
  infoPanelField: {
    display: 'flex',
    flexDirection: 'row',
    position: 'relative',
    gap: '2px',
    width: '100%',
    padding: '2px',
    borderTop: '1px solid #a5acaf',
    boxSizing: 'border-box',
  },
  dateField: {
    position: 'relative',
    width: '30%',
    display: 'flex',
    flexDirection: 'column',
  },
  fieldItemContainer: {
    position: 'relative',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
  },
  textSmall: {
    marginTop: '8px',
    fontSize: '10px',
    textAlign: 'left',
  },
  textMedium: {
    fontSize: '12px',
    marginTop: '6px',
    textAlign: 'left',
  },
  uploadedImageContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '4px',
    borderTop: '1px solid #a5acaf',
    marginBottom: '4px',
  },
  uploadedImage: {
    width: '100%',
    maxHeight: '80px',
    objectFit: 'contain',
    marginBottom: '2px',
  },
});

export async function exportCadDrawingPdfReport(
  image: CadDrawingReportImage,
  options: InfoPanelOptions,
  backgroundColor?: string,
) {
  const component = <CadDrawingPdfReport image={image} options={options} preview={false} />;

  const result = pdf(component);
  const blob = await result.toBlob();

  downloadBlob(
    blob,
    `${normalizeString(`${projectStateAdapter.getSnapshot().project?.name ?? 'report'}_${formattedDate(Date.now())}`)}`,
    'pdf',
  );
}

type ProfileLineReportOptions = {
  legendTitle: string;
  legendItems: GraphLine[];
  footer: {
    title: string;
    author: string;
    description?: string;
    profileLineName?: string;
    totalLength?: string;
  };
};

export async function exportProfileLinePdfReportImg(
  svg: SVGSVGElement,
  options: ProfileLineReportOptions,
) {
  const graphCanvas = await profileReportUtils.svgToPng(svg);
  const url = graphCanvas.toDataURL();
  const component = <ProfileLinePdfReport imageUrls={[url]} options={options} />;

  const result = pdf(component);
  const blob = await result.toBlob();

  downloadBlob(
    blob,
    `${normalizeString(`${options.footer.title}_${projectStateAdapter.getSnapshot().project?.name ?? 'report'}_${formattedDate(Date.now())}`)}`,
    'pdf',
  );
}

export async function exportProfileLinePdfReport(
  svgs: JSX.Element[],
  options: ProfileLineReportOptions,
  rowsPerPage: number,
) {
  const component = <ProfileLinePdfReportSvg options={options} rows={rowsPerPage} svgs={svgs} />;

  const result = pdf(component);
  const blob = await result.toBlob();

  downloadBlob(
    blob,
    `${normalizeString(`${options.footer.title}_${projectStateAdapter.getSnapshot().project?.name ?? 'report'}_${formattedDate(Date.now())}`)}`,
    'pdf',
  );
}
